<template>
  <BaseTabsBlock
    v-if="oldTabs"
    v-bind="$attrs"
    :no-box-container="noBoxContainer"
    :keep-tabs="keepTabs"
    :options="options"
    :display-tab-content="displayTabContent"
    @on-set-active-tab="$emit('onSetActiveTab', $event)"
  >
    <template #default="{ activeTab: oldTabActiveOption }">
      <slot :active-tab="oldTabActiveOption"></slot>
    </template>
  </BaseTabsBlock>
  <div v-else v-query-params="tab" class="tabs">
    <div class="tabs__scroll-wrapper">
      <div class="tabs__tabs">
        <button
          v-for="(option, index) in allTabOptions"
          :key="index"
          :data-cy="option?.dataCy ?? ''"
          :aria-label="option.display"
          class="tabs__tab"
          :class="{
            'tabs__tab--active': isTabActive(index),
            'tabs__tab--chip': option.chip,
            hide: option.hideTab,
          }"
          @click="setActiveTab(index)"
        >
          <span class="tabs__tab-content">
            {{ option.display }}
            <BaseChip
              v-if="option.chip"
              :class="{ 'tabs__chip--loading': option.chip.isLoading }"
              :theme="option.chip.theme"
              size="tiny"
            >
              {{ option.chip.text }}
            </BaseChip>
          </span>
        </button>
      </div>
    </div>

    <div
      v-if="displayTabContent"
      :class="[
        'tabs__content',
        { 'tabs__content--no-box-container': noBoxContainer },
      ]"
    >
      <slot :active-tab="activeTabOption.value"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import BaseChip from '@/components/generics/base/BaseChip.vue'
import { useTabOptions } from '@/hooks/useTabOptions'
import { toRefs } from 'vue'

import type { BaseChipProps } from './BaseChip.vue'
import BaseTabsBlock from './BaseTabsBlock.vue'

interface TabChip {
  text: string | number
  theme?: BaseChipProps['theme']
  /** Shows the chip as a skeleton, sized by `text` */
  isLoading?: boolean
}

export interface TabOptions {
  display: string
  value: any
  dataCy?: string
  hideTab?: boolean
  chip?: TabChip
}

interface SetActiveTabEvent {
  index: number
  value: any
}

export interface BaseTabsProps {
  options: TabOptions[]
  noBoxContainer?: boolean
  displayTabContent?: boolean
  keepTabs?: boolean
  oldTabs?: boolean
  initialTabIndex?: number
  /**
   * Makes the strip controlled: it shows this index and only *asks* to change
   * via `onSetActiveTab`. For tabs that mirror a route, so a refused navigation
   * leaves the strip where the route is.
   */
  activeTabIndex?: number
}

const props = withDefaults(defineProps<BaseTabsProps>(), {
  noBoxContainer: false,
  displayTabContent: true,
  keepTabs: true,
  oldTabs: false,
  initialTabIndex: 0,
  activeTabIndex: undefined,
})

const emit = defineEmits<{
  onSetActiveTab: [event: SetActiveTabEvent]
}>()

const {
  activeTabOption,
  allTabOptions,
  tab,
  isTabActive,
  setActiveTab: setActiveTabInternal,
} = useTabOptions(toRefs(props))

const setActiveTab = (index: number): void => {
  setActiveTabInternal(index)

  emit('onSetActiveTab', {
    index,
    value: allTabOptions.value[index].value,
  })
}
</script>

<style lang="scss" scoped>
.hide {
  display: none;
}

.tabs {
  display: flex;
  flex-direction: column;
  // flex: 1 1 0;
  height: 100%;

  &__scroll-wrapper {
    display: flex;
    align-items: center;
    position: relative;
    gap: $spacing-4xs;
    overflow-x: auto;
    overflow-y: hidden;
    border-bottom: 1px solid $color-border;
  }

  &__tabs {
    display: flex;
    min-width: min-content;
    position: relative;
    gap: $spacing-3xs;
  }

  &__tab {
    color: $color-text-light;
    background-color: inherit;
    padding: $spacing-4xs;
    cursor: pointer;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    flex-shrink: 0;
    border: none;
    border-bottom: 2px solid transparent;

    &::after {
      content: attr(aria-label);
      height: 0;
      visibility: hidden;
      overflow: hidden;
      user-select: none;
      pointer-events: none;
      font-weight: 700;
    }

    &--chip::after {
      padding-right: calc($spacing-4xs + 25px);
    }

    &--active {
      color: $color-text;
      font-weight: 700;
      border-bottom: 2px solid $color-navigation;
      z-index: 1;
      background-color: $color-paper;
      transition: color 75ms ease-in-out, border-bottom 75ms ease-in-out;
    }
  }

  &__tab-content {
    display: flex;
    align-items: center;
    gap: $spacing-4xs;
    line-height: 24px;
  }

  &__chip--loading {
    color: transparent;
    border-color: transparent;
    @include skeletonLoadingAnimation;
  }

  &__content {
    display: flex;
    flex-direction: column;
    flex: 1 1 0;
  }

  &__content--no-box-container {
    background: none;
  }
}
</style>
